import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Cookie, ShieldCheck } from 'lucide-react';
import { useLang } from '../i18n';
import { useConsent } from '../consent';

export const CookieConsent = ({ onOpenLegal }) => {
  const { t } = useLang();
  const { consent, bannerOpen, settingsOpen, setSettingsOpen, acceptAll, acceptNecessary, persist } = useConsent();
  const [analytics, setAnalytics] = useState(false);

  // When the settings panel opens, reflect the user's current choice so the
  // toggle isn't misleadingly off for someone who already accepted analytics.
  useEffect(() => {
    if (settingsOpen) setAnalytics(!!(consent && consent.analytics));
  }, [settingsOpen, consent]);

  if (!bannerOpen && !settingsOpen) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4" data-testid="cookie-consent">
      <div className="mx-auto max-w-3xl rounded-2xl border bg-card p-4 shadow-[0_10px_40px_rgba(15,23,42,0.18)] sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-base font-semibold text-foreground">{t('cookie_banner_title')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('cookie_banner_text')}</p>

            {settingsOpen && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between rounded-lg border bg-secondary/40 px-3 py-2.5">
                  <div className="pr-3">
                    <div className="flex items-center gap-1.5 text-sm font-medium"><ShieldCheck className="h-4 w-4 text-primary" />{t('cookies_necessary')}</div>
                    <p className="text-xs text-muted-foreground">{t('cookies_necessary_desc')}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">{t('always_on')}</span>
                </div>
                <div className="flex items-center justify-between rounded-lg border bg-secondary/40 px-3 py-2.5">
                  <div className="pr-3">
                    <div className="text-sm font-medium">{t('cookies_analytics')}</div>
                    <p className="text-xs text-muted-foreground">{t('cookies_analytics_desc')}</p>
                  </div>
                  <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="consent-analytics-switch" />
                </div>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              {settingsOpen ? (
                <Button size="sm" onClick={() => persist(analytics)} data-testid="consent-save">{t('save_selection')}</Button>
              ) : (
                <>
                  <Button size="sm" onClick={acceptAll} data-testid="consent-accept-all">{t('accept_all')}</Button>
                  <Button size="sm" variant="outline" onClick={acceptNecessary} data-testid="consent-accept-necessary">{t('accept_necessary')}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSettingsOpen(true)} data-testid="consent-settings">{t('cookie_settings')}</Button>
                </>
              )}
              <button onClick={() => onOpenLegal('privacy')} className="ml-auto self-center text-xs text-muted-foreground underline hover:text-foreground">{t('privacy')}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
